import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import api from "@/lib/api";

const POPULAR_LOCALITIES = [
  "Baner", "Balewadi", "Wakad", "Hinjewadi", "Aundh",
  "Kharadi", "Viman Nagar", "Wagholi", "Magarpatta", "Hadapsar",
];
const localitySlug = (l) => l.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const POPULAR_FACETS = [
  { label: "2 BHK in Baner", href: "/pune/baner/2-bhk" },
  { label: "3 BHK in Kharadi", href: "/pune/kharadi/3-bhk" },
  { label: "Ready to Move in Wakad", href: "/pune/wakad/2-bhk/ready-to-move" },
  { label: "Under Construction in Hinjewadi", href: "/pune/hinjewadi/2-bhk/under-construction" },
  { label: "2 BHK in Viman Nagar", href: "/pune/viman-nagar/2-bhk" },
  { label: "3 BHK Ready to Move in Baner", href: "/pune/baner/3-bhk/ready-to-move" },
];

const DEVELOPERS_CACHE_KEY = "inprop.public.developers";
const DEVELOPERS_TTL_MS = 10 * 60 * 1000;

export default function PublicLayout({ children, footerNote }) {
  // Top developers rail — cached 10 min in localStorage; fetch is silent
  // and never blocks initial paint.
  const [developers, setDevelopers] = useState([]);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(DEVELOPERS_CACHE_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p && (Date.now() - p.t) < DEVELOPERS_TTL_MS && Array.isArray(p.developers)) {
          setDevelopers(p.developers);
          return;
        }
      }
    } catch (_e) { /* corrupt cache — ignore */ }

    api.get("/developers")
      .then(({ data }) => {
        const top = (data.developers || []).slice(0, 12);
        setDevelopers(top);
        try {
          window.localStorage.setItem(DEVELOPERS_CACHE_KEY, JSON.stringify({ t: Date.now(), developers: top }));
        } catch (_e) { /* quota — ignore */ }
      })
      .catch(() => setDevelopers([]));
  }, []);

  return (
    <div className="min-h-screen bg-sg-paper flex flex-col">
      <PublicNav />
      <main className="flex-1">{children}</main>
      <footer className="border-t border-sg-line py-10 bg-sg-paper">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-6 text-sm text-sg-mute">
          <div>
            <img
              src="/inpropportal-logo.png"
              alt="INPropPortal.com"
              className="h-12 w-auto object-contain mb-2"
              data-testid="footer-brand-logo"
            />
            <p className="mt-2 text-xs">© 2026 · Pune, Maharashtra · IN</p>
            <p className="text-xs mt-1 font-mono">MahaCRITI-aligned</p>
            <div className="mt-4 flex items-center gap-2" data-testid="footer-social">
              <a
                href="https://www.instagram.com/INPropPortal"
                target="_blank"
                rel="noreferrer"
                aria-label="INPropPortal on Instagram"
                className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-sg-line text-sg-mute hover:text-sg-navy hover:border-sg-gold-deep transition-colors"
                data-testid="footer-social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
          <div>
            <div className="overline text-sg-gold-deep mb-2">EXPLORE</div>
            <ul>
              <li><Link to="/buy" className="block py-3 hover:text-sg-navy">Buy</Link></li>
              <li><Link to="/rent" className="block py-3 hover:text-sg-navy">Rent</Link></li>
              <li><Link to="/new-launches" className="block py-3 hover:text-sg-navy">New Launches</Link></li>
              <li><Link to="/market-insights" className="block py-3 hover:text-sg-navy">Market Insights</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-sg-gold-deep mb-2">SELL & LEASE</div>
            <ul>
              <li><Link to="/sell" className="block py-3 hover:text-sg-navy">Sell My Property</Link></li>
              <li><Link to="/sell" className="block py-3 hover:text-sg-navy">Lease Out My Unit</Link></li>
              <li><Link to="/financials" className="block py-3 hover:text-sg-navy">Financials</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-sg-gold-deep mb-2">PORTAL</div>
            <ul>
              <li><Link to="/about" className="block py-3 hover:text-sg-navy">About Us</Link></li>
              <li><Link to="/login" className="block py-3 hover:text-sg-navy">For Agents</Link></li>
              <li><Link to="/terms" className="block py-3 hover:text-sg-navy" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
              <li><a href="mailto:hello@inpropportal.com" className="block py-3 hover:text-sg-navy">Contact</a></li>
            </ul>
          </div>
        </div>

        {/* Popular locality searches — SEO internal linking */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-10 border-t border-sg-line pt-8" data-testid="footer-popular-searches">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="overline text-sg-gold-deep mb-3">POPULAR LOCALITIES · PUNE</div>
              <div className="flex flex-wrap gap-1.5">
                {POPULAR_LOCALITIES.map((l) => (
                  <Link key={l} to={`/pune/${localitySlug(l)}`}
                        className="text-[11px] px-2.5 py-1 rounded-full border border-sg-line hover:border-sg-gold-deep hover:text-sg-navy text-sg-mute"
                        data-testid={`footer-locality-${localitySlug(l)}`}>
                    {l}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className="overline text-sg-gold-deep mb-3">POPULAR SEARCHES</div>
              <div className="flex flex-wrap gap-1.5">
                {POPULAR_FACETS.map((f) => (
                  <Link key={f.href} to={f.href}
                        className="text-[11px] px-2.5 py-1 rounded-full bg-sg-cream-soft border border-sg-line hover:border-sg-gold-deep hover:text-sg-navy text-sg-mute"
                        data-testid={`footer-facet-${f.href.replace(/[^a-z0-9]+/gi, "-")}`}>
                    {f.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 text-xs text-sg-mute/70">
            <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="hover:text-sg-navy" data-testid="footer-sitemap-link">Sitemap</a>
          </div>
        </div>

        {/* Developer directory — dynamic top 12 by project count */}
        {developers.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-8 border-t border-sg-line pt-8" data-testid="footer-developers">
            <div className="overline text-sg-gold-deep mb-3">TOP DEVELOPERS IN PUNE</div>
            <div className="flex flex-wrap gap-1.5">
              {developers.map((d) => (
                <Link
                  key={d.slug}
                  to={`/developer/${d.slug}`}
                  className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-sg-line hover:border-sg-gold-deep hover:text-sg-navy text-sg-mute"
                  data-testid={`footer-developer-${d.slug}`}
                >
                  {d.name} <span className="text-sg-line ml-0.5">·</span> <span className="text-sg-navy/60 font-mono ml-0.5">{d.project_count}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {footerNote && (
          <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-8 text-xs text-sg-mute/80 border-t border-sg-line pt-6">{footerNote}</div>
        )}
      </footer>
    </div>
  );
}
